import { css, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { IconName } from '@shared/components/core/images/IconMap';
import Icons from '@shared/components/core/images/Icons';
import { NavLink } from 'react-router';
import { MixinFocusVisible } from '../structure/DefaultStyles';

type TextButtonVariant = 'default' | 'context' | 'control' | 'negative' | 'subsection';
type ButtonType = 'button' | 'submit' | 'reset';

interface ITextButtonProps {
    variant?: TextButtonVariant;
    disabled?: boolean;
    startIcon?: IconName;
    endIcon?: IconName;
    children: React.ReactNode;
    target?: string;
    href?: string;
    to?: string;
    type?: ButtonType;
    viewTransition?: boolean;
    onClick?: (
        event:
            | React.MouseEvent<HTMLButtonElement>
            | React.KeyboardEvent<HTMLButtonElement>
            | React.TouchEvent<HTMLButtonElement>
    ) => void;
    [key: string]: any; // for any other props like data-*, aria-*
}

function TextButton({
    type = 'button',
    variant = 'default',
    disabled = false,
    onClick,
    href,
    to,
    target,
    startIcon,
    endIcon,
    children,
    ...restProps
}: ITextButtonProps) {
    const theme = useTheme();
    const commonProps = {
        variant,
        disabled,
        target,
    };
    const buttonBaseStyles = css`
        align-items: center;
        background-color: transparent;
        border: none;
        cursor: pointer;
        font-family: 'Archivo', sans-serif;
        padding: 0.25rem 1rem 0.25rem 0;
        ${MixinFocusVisible}
        @media (prefers-reduced-motion: no-preference) {
            transition: all 0.1s ease-out;
        }
        svg {
            vertical-align: middle;
            scale: 0.8;
        }
        :disabled,
        :disabled * {
            cursor: not-allowed !important;
            opacity: 0.9;
            color: #666;
            border-color: #666;
            &:hover {
                color: #666;
            }
        }
    `;
    const controlButtonStyles = css`
        align-items: center;
        color: ${theme.color.textBody};
        display: inline-flex;
        font-size: 1rem; // 16px
        font-weight: 500;
        justify-content: center;
        line-height: 1.313rem; // 21px
        :hover {
            cursor: pointer;
            color: ${theme.color.link};
            text-decoration: underline;
        }
    `;
    const subsectionButtonStyles = css`
        align-items: center;
        color: ${theme.color.link};
        display: inline-flex;
        font-size: 1rem; // 16px
        font-weight: 400;
        justify-content: center;
        line-height: 1.313rem; // 21px
        text-decoration: underline !important;
        :hover {
            cursor: pointer;
            color: ${theme.color.link};
            text-decoration: underline;
        }
    `;
    const defaultButtonStyles = css`
        align-items: center;
        color: ${theme.color.textBody};
        display: inline-flex;
        font-size: 0.875rem;
        font-weight: 500;
        gap: 0.25rem;
        justify-content: center;
        :hover {
            color: ${theme.color.link};
            text-decoration: underline;
        }
    `;
    const contextButtonStyles = css`
        color: ${theme.color.link};
        font-size: 1.125rem;
        font-weight: 400;
        line-height: 1.625rem;
        text-decoration: underline !important;
        :hover {
            color: ${theme.color.text};
            text-decoration: none;
        }
    `;
    const negativeButtonStyles = css`
        align-items: center;
        color: ${theme.banner.error.linkText};
        display: inline-flex;
        font-size: 0.875rem;
        font-weight: 500;
        gap: 0.25rem;
        justify-content: center;
        :hover {
            color: ${theme.color.link};
            text-decoration: underline;
        }
    `;

    const StyledButton = styled.button<ITextButtonProps>`
        ${buttonBaseStyles}
        ${(props) => props.variant === 'default' && defaultButtonStyles}
        ${(props) => props.variant === 'context' && contextButtonStyles}
        ${(props) => props.variant === 'control' && controlButtonStyles}
        ${(props) => props.variant === 'negative' && negativeButtonStyles}
        ${(props) => props.variant === 'subsection' && subsectionButtonStyles}
    `;
    const StyledLink = styled.a<ITextButtonProps>`
        ${buttonBaseStyles}
        ${(props) => props.variant === 'default' && defaultButtonStyles}
        ${(props) => props.variant === 'context' && contextButtonStyles}
        ${(props) => props.variant === 'control' && controlButtonStyles}
        ${(props) => props.variant === 'negative' && negativeButtonStyles}
        ${(props) => props.variant === 'subsection' && subsectionButtonStyles}

        visibility: ${(props) => (props.disabled ? 'hidden' : 'visible')};
    `;

    const StyledNavLink = styled(NavLink)<ITextButtonProps>`
        ${buttonBaseStyles}
        ${(props) => props.variant === 'default' && defaultButtonStyles}
        ${(props) => props.variant === 'context' && contextButtonStyles}
        ${(props) => props.variant === 'control' && controlButtonStyles}
        ${(props) => props.variant === 'negative' && negativeButtonStyles}
        ${(props) => props.variant === 'subsection' && subsectionButtonStyles}
    `;

    endIcon = target === '_blank' ? 'externalLink' : endIcon;
    if (to) {
        return (
            <StyledNavLink className="text-button" to={to} {...commonProps} {...restProps}>
                {startIcon && <Icons name={startIcon} />}
                {children}
                {endIcon && <Icons name={endIcon} />}
            </StyledNavLink>
        );
    }

    if (href) {
        return (
            <StyledLink className="text-button" href={href} {...commonProps} {...restProps}>
                {startIcon && <Icons name={startIcon} />}
                {children}
                {endIcon && <Icons name={endIcon} />}
            </StyledLink>
        );
    } else {
        return (
            <StyledButton className="text-button" type={type} onClick={onClick} {...commonProps} {...restProps}>
                {startIcon && <Icons name={startIcon} />}
                {children}
                {endIcon && <Icons name={endIcon} />}
            </StyledButton>
        );
    }
}

export default TextButton;
